import { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { useAppStore } from  "@/hooks/use-app-store"
import { DEAIVisionWebClient } from '@akavirtual/deai-web-core';

const defaultPhonemeMultipliers = new Map<string, number>();
defaultPhonemeMultipliers.set("aa", 1);
defaultPhonemeMultipliers.set("ih", 1);
defaultPhonemeMultipliers.set("ou", 1);
defaultPhonemeMultipliers.set("ee", 1);
defaultPhonemeMultipliers.set("oh", 1);

interface UseDEAI3DSceneOptions {
  phonemeMultipliers?: Map<string, number>;
}

export const useDEAI3DScene = (options: UseDEAI3DSceneOptions = {}) => {
  // Ref to hold the DEAIVisionWebClient instance
  const deaiSceneRef = useRef<DEAIVisionWebClient>(null);

  // Use provided phonemeMultipliers or fallback to defaults
  const phonemeMultipliers = useMemo(() => {
    return options.phonemeMultipliers || defaultPhonemeMultipliers;
  }, [options.phonemeMultipliers]);

  // State to track if the 3D model has finished loading
  const [isModelLoaded, setIsModelLoaded] = useState(false);

  // Expose ref globally for debugging (development only)
  useEffect(() => {
    if (typeof window !== 'undefined') {
      (window as any).debugDEAIRef = deaiSceneRef;
    }
  }, []);

  // Get subscription methods from the global app store
  const { sub, unsub } = useAppStore();

  // --- Message Handlers ---
  // Memoized handlers to dispatch events to the 3D scene.
  // useCallback ensures these functions are not recreated on every render.

  const handleMessage = useCallback((payload?: object) => {
    deaiSceneRef.current?.dispatchMessage('message', payload);
  }, []);

  const handleAudioMessage = useCallback((payload?: object) => {
    deaiSceneRef.current?.dispatchMessage('audio', payload);
  }, []);

  const handleSystemMessage = useCallback((payload?: object) => {
    deaiSceneRef.current?.dispatchMessage('system_event', payload);
  }, []);

  const handleVisionMessage = useCallback((payload?: object) => {
    const p = payload as { cmd: string; payload?: object };
    if (p?.cmd) {
      deaiSceneRef.current?.dispatchMessage(p.cmd, p.payload);
    }
  }, []);

  // --- Side Effects ---
  // Subscribes to global app events on mount and unsubscribes on unmount.
  useEffect(() => {
    sub('message', handleMessage);
    sub('system_event', handleSystemMessage);
    sub('audio', handleAudioMessage);
    sub('vision', handleVisionMessage);

    // Cleanup function to run when the component unmounts
    return () => {
      unsub('message', handleMessage);
      unsub('system_event', handleSystemMessage);
      unsub('audio', handleAudioMessage);
      unsub('vision', handleVisionMessage);
    };
  }, [sub, unsub, handleMessage, handleAudioMessage, handleSystemMessage, handleVisionMessage]);
  
  // Callback to be passed to the component, updating the loading state.
  const onModelInitialized = useCallback(() => {
    setIsModelLoaded(true);
    const avatar = deaiSceneRef.current?._webVision?.getAvatar(0);
    if(avatar) {
      avatar.setAttribute("phoneme_multipliers",phonemeMultipliers);
      console.log(avatar.getAttribute("phoneme_multipliers"));
    }
  }, []);

  // Expose the necessary state and handlers to the UI component
  return {
    deaiSceneRef,
    isModelLoaded,
    onModelInitialized,
  };
};